import { HeroBackground } from '@prishapolicy/shared/ui';
import { Button, Modal, showToast, ButtonPanel } from '@prishapolicy/shared/ui';
import { NextSeo } from 'next-seo';
import Link from 'next/link';
import { ChangeEvent, useState } from 'react';

type DocumentItem = {
  title: string;
  description: string;
  path?: string;
  comingSoon?: true;
  showUploadButton?: boolean;
};

type ClaimProcessGroup = {
  section: string;
  documents: DocumentItem[];
};

const claimProcessSections: ClaimProcessGroup[] = [
      {
        section: 'Project and Engineering',
        documents: [
          {
            title: 'Claim Incident Note',
            description: 'Template to record incident details for project and engineering claims.',
            path: '/resources/claim-processes/project-and-engineering/claim-incident-note.docx',
            showUploadButton: true,
          },
          {
            title: 'Claims Procedure (English)',
            description: 'Read the claims procedure in English for project and engineering claims.',
            path: '/resources/claim-procedure/project-and-engineering/Insurance_Claim_Reporting_Process_Final.pptx',
            showUploadButton: false,
          },
          {
            title: 'Claims Procedure (Hindi)',
            description: 'Hindi version will be added soon.',
            comingSoon: true,
          },
          {
            title: 'Claims Procedure (Marathi)',
            description: 'Marathi version will be added soon.',
            comingSoon: true,
          },
        ],
      },
  {
    section: 'Motor Claims',
    documents: [
      {
        title: 'Road Accident Claim Checklist',
        description: 'Document list and filing sequence for a motor claim settlement.',
        comingSoon: true,
      },
      {
        title: 'Claim Timeline Guide',
        description: 'Step-by-step timeline from intimation to settlement for motor incidents.',
        comingSoon: true,
      },
    ],
  },
  {
    section: 'Health and Personal Accident',
    documents: [
      {
        title: 'Hospitalization Claim Dossier',
        description: 'Checklist for hospitalization and cashless claim documents.',
        comingSoon: true,
      },
    ],
  },
  {
    section: 'Travel and Other Personal Claims',
    documents: [
      {
        title: 'Personal Claim Filing Checklist',
        description: 'Preparation checklist for miscellaneous personal claim filings.',
        comingSoon: true,
      },
    ],
  },
];

const ClientServicingResources = () => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith('.docx')) {
      showToast({ variant: 'warning', title: 'Invalid file type', description: 'Upload only .docx files.' });
      event.target.value = '';
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const closeUploadModal = () => {
    if (isSubmitting) return;
    setIsUploadModalOpen(false);
    setSelectedFile(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast({ variant: 'warning', title: 'No file selected', description: 'Please choose a filled .docx file before submitting.' });
      return;
    }

    const reader = new FileReader();
    setIsSubmitting(true);

    const base64Content = await new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string' || !result.includes(',')) {
          reject(new Error('Unable to read file.'));
          return;
        }
        resolve(result.split(',')[1]);
      };
      reader.onerror = () => reject(new Error('Unable to read file.'));
      reader.readAsDataURL(selectedFile);
    });

    try {
      const response = await fetch('/api/client-servicing-resources/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          fileContent: base64Content,
        }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const errorMessage = data?.error || 'Unable to send your form right now.';
        throw new Error(errorMessage);
      }

      showToast({
        variant: 'success',
        title: 'Submitted',
        description: 'Your form has been emailed to the claims team.',
      });
      closeUploadModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      showToast({ variant: 'danger', title: 'Upload failed', description: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <NextSeo
        title="Client Servicing Resources | PrishaPolicy"
        description="Claim process playbooks, templates, and supporting documents for faster client servicing workflows."
      />
      <HeroBackground backgroundColor="yellow-200">
        <div className="mt-16 mb-9 text-center lg:mx-auto lg:w-1/2">
          <h1 className="heading-1">Client Servicing Resources</h1>
          <h5 className="heading-5 mt-6 mb-6">Claim documents, templates, and process guides for your team.</h5>
          <div>
            <Link
              href="/resources/insurance-resources/insurer-resources"
              className="text-body-5 inline-block font-semibold underline hover:no-underline"
            >
              Go to Insurer Resources
            </Link>
          </div>
        </div>
      </HeroBackground>

      <section className="container pb-16">
        <h2 className="heading-2 mt-4">Claim Processes</h2>
        <p className="text-grey-700 mt-3 mb-8 max-w-3xl">
          Downloadable documents and process notes to support claim handling across segments.
        </p>
        <div className="grid gap-5 lg:grid-cols-2">
          {claimProcessSections.map((group) => (
            <div key={group.section} className="rounded-large border-grey-200 bg-surface-grey border p-4">
              <h3 className="heading-6 mb-3">{group.section}</h3>
              <div className="space-y-3">
                {group.documents.map((doc) => (
                  <div key={doc.title} className="rounded-medium border-grey-200 bg-white p-4">
                    <h4 className="font-semibold">{doc.title}</h4>
                    <p className="text-grey-700 mt-1 text-sm">{doc.description}</p>
                    {doc.comingSoon ? (
                      <p className="text-body-6 mt-2 font-semibold text-grey-500">Coming soon</p>
                    ) : (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <a
                          href={doc.path}
                          download
                          className="text-body-5 inline-flex text-primary underline hover:no-underline"
                        >
                          Download file
                        </a>
                        {doc.showUploadButton !== false && (
                          <Button
                            text="Upload filled form"
                            variant="secondary"
                            size="small"
                            onClick={() => setIsUploadModalOpen(true)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Modal
        isOpen={isUploadModalOpen}
        closeModal={closeUploadModal}
        title="Upload filled claim form"
        description="Upload your completed Claim Incident Note (.docx) file. We will email it to the claims team."
      >
        <div className="mx-auto max-w-xl px-6 py-2">
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileChange}
            className="block w-full text-body-3 text-grey-700 file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-white"
          />
          {selectedFile ? (
            <p className="text-body-6 mt-3 text-grey-700">Selected file: {selectedFile.name}</p>
          ) : (
            <p className="text-body-6 mt-3 text-grey-500">No file selected</p>
          )}
          <ButtonPanel
            primaryButtonInfo={{
              text: isSubmitting ? 'Uploading...' : 'Upload and email',
              type: 'button',
              onClick: handleUpload,
              disabled: isSubmitting,
            }}
            tertiaryButtonInfo={{
              text: 'Cancel',
              onClick: closeUploadModal,
              disabled: false,
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default ClientServicingResources;
